import React, { useEffect, useRef, useState } from 'react';

import googleAnalyticsLogo from 'assets/images/new-home-page/tools-integrations/google-analytics-logo.svg';
import googleSheetsLogo from 'assets/images/new-home-page/tools-integrations/google-sheets-logo.svg';
import hubspotLogo from 'assets/images/new-home-page/tools-integrations/hubspot-logo.svg';
import salesforceLogo from 'assets/images/new-home-page/tools-integrations/salesforce-logo.svg';
import zapierLogo from 'assets/images/new-home-page/tools-integrations/zapier-logo.svg';
import ecosystemImages from 'assets/images/integrations/ecosystem';
import PageLayout from 'components/common/PageLayout';
import { API_ROUTE, ENTERPRISE_CONTACT_LINK } from 'components/constants';

import styles from './Integrations.module.scss';

const connectionPaths = [
  {
    number: '01',
    label: 'Built into every product',
    title: 'Configure common connections without a custom build.',
    text: 'Add analytics, content import, user-data export, scripts, trackers, webhooks and URL parameters from the product integration panel.',
  },
  {
    number: '02',
    label: 'Automation and routing',
    title: 'Move signals into the tools that run the next step.',
    text: 'Use webhooks and automation platforms to route submissions, choices and journey events into operational workflows.',
  },
  {
    number: '03',
    label: 'Custom API client',
    title: 'Connect to internal systems on your own terms.',
    text: 'Define the events, payload and endpoint for your backend, CRM, data platform or internal application.',
  },
];

const builtInConnections = [
  {
    title: 'Google Analytics',
    description: 'Send Storycards engagement and journey events into your existing analytics setup.',
    icon: googleAnalyticsLogo,
    iconAlt: 'Google Analytics',
  },
  {
    title: 'Google Sheets: Import content',
    description: 'Bring structured content into a product from a shared Google Sheet.',
    icon: googleSheetsLogo,
    iconAlt: 'Google Sheets',
  },
  {
    title: 'Google Sheets: Export users data',
    description: 'Export collected user data into a spreadsheet your teams can access.',
    icon: googleSheetsLogo,
    iconAlt: 'Google Sheets',
  },
  {
    title: 'Script',
    description: 'Add approved custom code when a product needs logic beyond a standard connection.',
    glyph: '</>',
  },
  {
    title: 'Custom tracker',
    description: 'Connect the tracking setup and event definitions used by your organization.',
    glyph: 'CT',
  },
  {
    title: 'Webhooks',
    description: 'Send selected product events to a destination URL as they happen.',
    glyph: 'WH',
  },
  {
    title: 'Passing URL Parameters',
    description: 'Pass approved context into the experience and preserve it through the journey.',
    glyph: '?=',
  },
  {
    title: 'Google Publisher Tag',
    description: 'Connect the product to the publisher ad setup used across your digital properties.',
    glyph: 'GPT',
  },
  {
    title: 'Outbrain footer widget',
    description: 'Add an Outbrain footer placement where it belongs in the audience journey.',
    glyph: 'OB',
  },
];

const dataFlows = [
  {
    eyebrow: 'Bring context in',
    title: 'Start the experience with the right content and context.',
    text: 'Import structured content, pass campaign or customer context through URL parameters, or use your own systems to provide the data the journey needs.',
    items: ['Structured content', 'Campaign parameters', 'Known customer context'],
  },
  {
    eyebrow: 'Storycards experience layer',
    title: 'Run the interactive product inside your existing stack.',
    text: 'Storycards brings content, logic and live interaction together in one governed product while your surrounding systems stay in place.',
    items: ['Content and logic together', 'Live responses captured', 'Managed in one platform'],
  },
  {
    eyebrow: 'Send signals out',
    title: 'Return interaction data to the systems that act on it.',
    text: 'Send answers, navigation, submissions, scores and completion events to analytics, CRM, automation, data and internal systems.',
    items: ['First-party responses', 'Journey events', 'Completion and intent'],
  },
];

const operatingModel = [
  {
    title: 'Product and marketing teams',
    text: 'Choose and configure supported connections for each product without changing the creative workflow in Studio.',
  },
  {
    title: 'Developers and platform teams',
    text: 'Establish reusable endpoints, payload rules and custom logic for the systems your organization controls.',
  },
  {
    title: 'Data and operations teams',
    text: 'Keep analytics, customer records and workflow systems as the source of truth while Storycards supplies structured interaction data.',
  },
];

const destinationLogos = [
  { src: salesforceLogo, alt: 'Salesforce' },
  { src: googleAnalyticsLogo, alt: 'Google Analytics' },
  { src: hubspotLogo, alt: 'HubSpot' },
  { src: zapierLogo, alt: 'Zapier' },
];

type EcosystemLogo = {
  id: string;
  name: string;
  src: string;
  scale?: number;
};

const VISIBLE_ECOSYSTEM_LOGOS = 8;
const ECOSYSTEM_INITIAL_HOLD = 2600;
const ECOSYSTEM_SWAP_INTERVAL = 5400;
const ECOSYSTEM_TRANSITION_DURATION = 960;

const ecosystemLogos: EcosystemLogo[] = [
  { src: salesforceLogo, id: 'salesforce', name: 'Salesforce', scale: 0.92 },
  { src: googleAnalyticsLogo, id: 'google-analytics', name: 'Google Analytics', scale: 0.74 },
  { src: googleSheetsLogo, id: 'google-sheets', name: 'Google Sheets', scale: 0.68 },
  { src: zapierLogo, id: 'zapier', name: 'Zapier', scale: 1.04 },
  { src: hubspotLogo, id: 'hubspot', name: 'HubSpot', scale: 0.76 },
  { src: ecosystemImages.make, id: 'make', name: 'Make', scale: 0.66 },
  { src: ecosystemImages.snowflake, id: 'snowflake', name: 'Snowflake', scale: 0.68 },
  { src: ecosystemImages.googleBigQuery, id: 'google-bigquery', name: 'Google BigQuery', scale: 0.68 },
  { src: ecosystemImages.googleTagManager, id: 'google-tag-manager', name: 'Google Tag Manager', scale: 0.68 },
  { src: ecosystemImages.airtable, id: 'airtable', name: 'Airtable', scale: 0.66 },
  { src: ecosystemImages.contentful, id: 'contentful', name: 'Contentful', scale: 0.66 },
  { src: ecosystemImages.wordpress, id: 'wordpress', name: 'WordPress', scale: 0.68 },
  { src: ecosystemImages.mailchimp, id: 'mailchimp', name: 'Mailchimp', scale: 0.68 },
  { src: ecosystemImages.meta, id: 'meta', name: 'Meta', scale: 0.72 },
  { src: ecosystemImages.googleAds, id: 'google-ads', name: 'Google Ads', scale: 0.68 },
  { src: ecosystemImages.databricks, id: 'databricks', name: 'Databricks', scale: 0.68 },
  { src: ecosystemImages.notion, id: 'notion', name: 'Notion', scale: 0.62 },
  { src: ecosystemImages.jira, id: 'jira', name: 'Jira', scale: 0.64 },
  { src: ecosystemImages.intercom, id: 'intercom', name: 'Intercom', scale: 0.64 },
  { src: ecosystemImages.zendesk, id: 'zendesk', name: 'Zendesk', scale: 0.64 },
  { src: ecosystemImages.mixpanel, id: 'mixpanel', name: 'Mixpanel', scale: 0.66 },
  { src: ecosystemImages.mongodb, id: 'mongodb', name: 'MongoDB', scale: 0.66 },
  { src: ecosystemImages.sap, id: 'sap', name: 'SAP', scale: 0.7 },
  { src: ecosystemImages.looker, id: 'looker', name: 'Looker', scale: 0.66 },
];

const ecosystemGroups = Array.from(
  { length: Math.ceil(ecosystemLogos.length / VISIBLE_ECOSYSTEM_LOGOS) },
  (_, groupIndex) =>
    Array.from(
      { length: VISIBLE_ECOSYSTEM_LOGOS },
      (__, slotIndex) => ecosystemLogos[(groupIndex * VISIBLE_ECOSYSTEM_LOGOS + slotIndex) % ecosystemLogos.length],
    ),
);

const ecosystemLogoStyle = (logo: EcosystemLogo) => ({ '--logo-scale': logo.scale || 1 } as React.CSSProperties);

const IntegrationEcosystem: React.FC = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [groupIndex, setGroupIndex] = useState(0);
  const [isSwapping, setIsSwapping] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const currentGroup = ecosystemGroups[groupIndex];
  const nextGroup = ecosystemGroups[(groupIndex + 1) % ecosystemGroups.length];

  useEffect(() => {
    ecosystemLogos.forEach(({ src }) => {
      const image = new Image();
      image.src = src;
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setIsInView(true);
      return undefined;
    }

    let initialTimer: number | undefined;
    let interval: number | undefined;
    let settleTimer: number | undefined;
    let observer: IntersectionObserver | undefined;
    let hasStarted = false;

    const swapLogos = () => {
      setIsSwapping(true);
      settleTimer = window.setTimeout(() => {
        setGroupIndex((currentIndex) => (currentIndex + 1) % ecosystemGroups.length);
        setIsSwapping(false);
      }, ECOSYSTEM_TRANSITION_DURATION);
    };

    const startRotation = () => {
      if (hasStarted) return;
      hasStarted = true;
      setIsInView(true);
      initialTimer = window.setTimeout(() => {
        swapLogos();
        interval = window.setInterval(swapLogos, ECOSYSTEM_SWAP_INTERVAL);
      }, ECOSYSTEM_INITIAL_HOLD);
    };

    if ('IntersectionObserver' in window && sectionRef.current) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          startRotation();
          observer?.disconnect();
        },
        { threshold: 0.25 },
      );
      observer.observe(sectionRef.current);
    } else {
      startRotation();
    }

    return () => {
      observer?.disconnect();
      if (initialTimer) window.clearTimeout(initialTimer);
      if (interval) window.clearInterval(interval);
      if (settleTimer) window.clearTimeout(settleTimer);
    };
  }, []);

  const renderLogo = (logo: EcosystemLogo, isHidden = false) => (
    <img
      src={logo.src}
      alt={isHidden ? '' : logo.name}
      className={logo.id === 'zapier' ? styles.ecosystemDarkLogo : undefined}
      style={ecosystemLogoStyle(logo)}
    />
  );

  return (
    <section ref={sectionRef} className={styles.ecosystemSection} aria-labelledby="ecosystem-title">
      <div className={styles.sectionInner}>
        <header className={styles.ecosystemHeader}>
          <div>
            <p className={styles.eyebrow}>Connected ecosystem</p>
            <h2 id="ecosystem-title">Connect the tools your teams already use.</h2>
          </div>
          <p>
            Use a built-in connection when it covers the workflow. For everything else, use webhooks, automation
            platforms or the Storycards API client to connect analytics, CRM, data, content and internal systems.
          </p>
        </header>

        <div
          className={`${styles.ecosystemLogoGrid} ${isInView ? styles.ecosystemInView : ''} ${
            isSwapping ? styles.ecosystemSwapping : ''
          }`}
          aria-label="Examples of systems and connection tools"
        >
          {currentGroup.map((logo, index) => {
            const nextLogo = nextGroup[index];
            const delayStyle = { '--slot-delay': `${index * 80}ms` } as React.CSSProperties;

            return (
              <div className={styles.ecosystemLogoSlot} key={`ecosystem-slot-${index}`} style={delayStyle}>
                <div className={`${styles.ecosystemLogoLayer} ${styles.ecosystemCurrentLogo}`}>{renderLogo(logo)}</div>
                <div className={`${styles.ecosystemLogoLayer} ${styles.ecosystemNextLogo}`} aria-hidden="true">
                  {renderLogo(nextLogo, true)}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.ecosystemNote}>
          <span>Analytics</span>
          <span>CRM</span>
          <span>Automation</span>
          <span>Data platforms</span>
          <span>Content systems</span>
          <span>Internal APIs</span>
          <p>Examples of systems and connection paths. Availability and setup method vary by integration.</p>
        </div>
      </div>
    </section>
  );
};

const Integrations: React.FC = () => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Enterprise integrations</p>
            <h1>Connect every product to your existing stack.</h1>
            <p className={styles.heroText}>
              Bring content and context into each experience, then send interaction data to your analytics, CRM,
              automation, data and internal systems. Start with built-in integrations or use the Storycards API client
              for a connection designed around your architecture.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
              <a href={API_ROUTE} className={styles.textLink}>
                Explore the API
              </a>
            </div>
          </div>

          <div className={styles.heroSummary}>
            <span>One integration layer</span>
            <div>
              <strong>Built-in connections</strong>
              <p>Configure common analytics, content and data workflows at product level.</p>
            </div>
            <div>
              <strong>Custom connections</strong>
              <p>Connect any enterprise system through webhooks or your own API client.</p>
            </div>
          </div>
        </div>

        <figure className={styles.integrationMap}>
          <figcaption>
            <span>Storycards integration layer</span>
            <em>Connected</em>
          </figcaption>
          <div className={styles.mapCanvas}>
            <div className={styles.productNode}>
              <span>Storycards product</span>
              <strong>One live experience</strong>
              <div>
                <i />
                <p>Content, context and live interaction events</p>
              </div>
            </div>

            <div className={styles.connectionNode}>
              <span>Choose a connection path</span>
              <div>
                <b>Built in</b>
                <small>No-code setup</small>
              </div>
              <div>
                <b>Webhook</b>
                <small>Event routing</small>
              </div>
              <div>
                <b>API client</b>
                <small>Custom logic</small>
              </div>
            </div>

            <div className={styles.destinationNode}>
              <span>Your enterprise stack</span>
              <div className={styles.logoGrid}>
                {destinationLogos.map((logo) => (
                  <div key={logo.alt}>
                    <img src={logo.src} alt={logo.alt} />
                  </div>
                ))}
                <div className={styles.systemTile}>BI</div>
                <div className={styles.systemTile}>Internal</div>
              </div>
            </div>
          </div>
        </figure>
      </section>

      <section className={styles.statement}>
        <div className={styles.sectionInner}>
          <h2>An interactive product should work inside your ecosystem, not beside it.</h2>
          <div>
            <p>
              Storycards is the experience layer. Your content systems can supply what the product needs, and your
              business systems can receive the structured signals people generate as they move through it.
            </p>
            <p>
              The same integration model applies across the Storycards products your organization creates. Teams can use
              a built-in connection when it fits, then move to a custom API implementation when the workflow or data
              policy requires more control.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.pathsSection}>
        <div className={styles.sectionInner}>
          <header className={styles.pathsHeader}>
            <p className={styles.eyebrow}>Three ways to connect</p>
            <h2>Use the connection path that fits each system.</h2>
            <p>
              Enterprise stacks are rarely uniform. Storycards supports fast product-level setup and custom technical
              integration within the same operating model.
            </p>
          </header>
          <div className={styles.pathGrid}>
            {connectionPaths.map((path) => (
              <article key={path.title}>
                <div>
                  <span>{path.number}</span>
                  <small>{path.label}</small>
                </div>
                <h3>{path.title}</h3>
                <p>{path.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <IntegrationEcosystem />

      <section className={styles.builtInSection}>
        <div className={styles.sectionInner}>
          <div className={styles.builtInCopy}>
            <p className={styles.eyebrow}>Built into the product</p>
            <h2>Common integrations are ready where teams need them.</h2>
            <p>
              Each Storycards product includes an integration panel. Teams can add the supported connection, configure
              it for that product and keep working in the same publishing flow.
            </p>
            <p className={styles.builtInNote}>
              Need a different destination or data model? Use webhooks or the API client without changing how the
              product is designed and managed.
            </p>
          </div>

          <div className={styles.integrationPanel} aria-label="Examples of integrations available inside Storycards">
            <div className={styles.panelHeader}>
              <div>
                <span>Product settings</span>
                <strong>Add integrations</strong>
              </div>
              <em>{builtInConnections.length} connection types</em>
            </div>
            <div className={styles.searchField}>
              <span>Search integrations</span>
              <i aria-hidden="true" />
            </div>
            <div className={styles.connectionList}>
              {builtInConnections.map((connection) => (
                <article key={connection.title}>
                  <div className={styles.connectionIcon}>
                    {connection.icon ? (
                      <img src={connection.icon} alt={connection.iconAlt} />
                    ) : (
                      <span>{connection.glyph}</span>
                    )}
                  </div>
                  <div>
                    <h3>{connection.title}</h3>
                    <p>{connection.description}</p>
                  </div>
                  <i className={styles.rowArrow} aria-hidden="true" />
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.flowSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>A two-way data layer</p>
              <h2>Connect what the experience knows with what your systems need.</h2>
            </div>
            <p>
              Integrations can shape the journey before someone interacts, capture what happens during the experience
              and route the result into the next business process.
            </p>
          </header>

          <div className={styles.flowJourney}>
            {dataFlows.map((flow, index) => (
              <article
                className={`${styles.flowStage} ${index === 1 ? styles.flowStageCore : ''}`}
                key={flow.title}
              >
                <div className={styles.flowStageHeader}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <small>{flow.eyebrow}</small>
                </div>
                <h3>{flow.title}</h3>
                <p>{flow.text}</p>
                <ul>
                  {flow.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.operatingSection}>
        <div className={styles.sectionInner}>
          <header className={styles.operatingHeader}>
            <p className={styles.eyebrow}>Designed for enterprise ownership</p>
            <h2>Business teams keep moving. Technical teams keep control.</h2>
            <p>Set the connection model once, then let each team operate in the part of the system it already owns.</p>
          </header>
          <div className={styles.operatingGrid}>
            {operatingModel.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Make Storycards part of your enterprise stack.</h2>
          <div>
            <p>
              Tell us which systems, events and workflows your products need to connect. We will help map the right
              built-in or custom integration path.
            </p>
            <div className={styles.ctaActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Plan your integration <span aria-hidden="true">→</span>
              </a>
              <a href={API_ROUTE} className={styles.textLink}>
                Explore the API
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default Integrations;
